/**
 * profile-helpers.ts — Hàm thuần cho Platform sửa hồ sơ tổ chức / nhân viên (tab Cài đặt, tab Nhân viên).
 * Server kiểm lại tất cả (backend `platform/org-profile-service.ts`, `org-profile-routes.ts`).
 */
import { COMPANY_KEYS, type CompanyKey, type MemberPatch, type OrgMember } from '@/hooks/use-platform-org-manage'
import { displayVnPhone, emailFormatError, memberPhoneError, normalizeEmail } from '@/pages/team/add-member-helpers'

export type CompanyForm = Record<CompanyKey, string>

export function emptyCompany(): CompanyForm {
  return Object.fromEntries(COMPANY_KEYS.map((k) => [k, ''])) as CompanyForm
}

/** Các ô thông tin công ty đã đổi so với giá trị đang lưu (so sau khi bỏ khoảng trắng hai đầu). */
export function companyChanges(form: CompanyForm, saved: Partial<CompanyForm> | undefined): Partial<CompanyForm> {
  const out: Partial<CompanyForm> = {}
  for (const k of COMPANY_KEYS) {
    const v = (form[k] ?? '').trim()
    if (v !== (saved?.[k] ?? '').trim()) out[k] = v
  }
  return out
}

/** Lỗi ô thông tin công ty (null = hợp lệ). Khớp kiểm của server. */
export function companyFieldError(key: CompanyKey, raw: string): string | null {
  const v = raw.trim()
  if (!v) return null
  if (key === 'company.email') return emailFormatError(normalizeEmail(v))
  if (key === 'company.phone' && (!/^\+?[\d\s.()-]+$/.test(v) || v.replace(/\D/g, '').length < 3)) {
    return 'Chỉ gồm số và + . ( ) -'
  }
  if (key === 'company.taxCode' && !/^\d{10}(-\d{3})?$|^\d{12}$/.test(v)) return 'Mã số thuế gồm 10 số, 10-3 số hoặc 12 số'
  return null
}

export interface MemberForm { fullName: string; email: string; phone: string; roleId: string; teamId: string }

export function memberFormOf(m: OrgMember): MemberForm {
  return {
    fullName: m.fullName, email: m.email, phone: displayVnPhone(m.phone),
    roleId: m.roleId ?? '', teamId: m.teamId ?? '',
  }
}

/** Phần đã đổi của nhân viên → body PATCH (chỉ gửi trường đổi). */
export function memberChanges(form: MemberForm, m: OrgMember): MemberPatch {
  const out: MemberPatch = {}
  const name = form.fullName.trim().replace(/\s+/g, ' ')
  if (name !== m.fullName) out.fullName = name
  const email = normalizeEmail(form.email)
  if (email !== m.email) out.email = email
  const phone = form.phone.trim()
  if (phone !== displayVnPhone(m.phone)) out.phone = phone || null
  if (form.roleId && form.roleId !== (m.roleId ?? '')) out.roleId = form.roleId
  if (form.teamId !== (m.teamId ?? '')) out.teamId = form.teamId || null
  return out
}

/** Lỗi từng ô của form nhân viên. */
export function memberFormErrors(form: MemberForm): Partial<Record<keyof MemberForm, string>> {
  const e: Partial<Record<keyof MemberForm, string>> = {}
  const name = form.fullName.trim()
  if (!name) e.fullName = 'Vui lòng nhập họ tên'
  else if (name.length > 100) e.fullName = 'Họ tên tối đa 100 ký tự'
  const emailErr = emailFormatError(normalizeEmail(form.email))
  if (emailErr) e.email = emailErr
  const phoneErr = memberPhoneError(form.phone)
  if (phoneErr) e.phone = phoneErr
  return e
}
